import {
  isEventSeen,
  isRecentlyChanged,
} from "features/CenterContent/RoleContent/EventHandling/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EventCardEvent } from "features/CenterContent/RoleContent/EventHandling/components/eventCard/types";
import { Nullish } from "shared/utils/objectUtils";
import {
  PlanEventInformation,
  PlanMeasureKey,
} from "features/CenterContent/RoleContent/EventHandling/components/EventChangesDialog/types";
import { useEventHandlingContext } from "features/CenterContent/RoleContent/EventHandling/EventHandlingContext";

export type MuteHistoryEntry = {
  timestamp: string;
  action: "muted" | "unmuted";
  reason?: string;
};

const getMuteHistoryStorageKey = (eventId: string) => `muteHistory:${eventId}`;

let MIGRATED = false;

function migrateMuteHistory() {
  if (typeof window === "undefined" || MIGRATED) return;

  const storageEntries = window.localStorage.length;
  for (let i = 0; i < storageEntries; i += 1) {
    const key = window.localStorage.key(i);

    if (
      key?.startsWith("infraEventMuteHistory:") ||
      key?.startsWith("trainEventMuteHistory:")
    ) {
      const entry = window.localStorage.getItem(key);

      const newKey = `muteHistory:${key.split(":")[1]}`;

      if (entry) {
        window.localStorage.setItem(newKey, entry);
        window.localStorage.removeItem(key);
      }
    }
  }
  MIGRATED = true;
}

export const isEventMuted = (eventId: string): boolean => {
  if (typeof window === "undefined") return false;

  // TODO: remove this in a few days
  migrateMuteHistory();

  try {
    const raw = window.localStorage.getItem(getMuteHistoryStorageKey(eventId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return false;
    const last = parsed[parsed.length - 1] as MuteHistoryEntry;
    return last.action === "muted";
  } catch {
    return false;
  }
};

export const useEventCardState = (
  event: EventCardEvent,
  previousEvent: Nullish<PlanEventInformation>,
  columnEventState?: PlanMeasureKey,
) => {
  // TODO: remove this in a few days
  migrateMuteHistory();

  const { notifyMuteChanged } = useEventHandlingContext();

  const [notifying, setNotifying] = useState(columnEventState === "noCoverage");
  const [muteDialogOpen, setMuteDialogOpen] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);

  const [muteHistory, setMuteHistory] = useState<MuteHistoryEntry[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(
        getMuteHistoryStorageKey(event.uuid),
      );
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed as MuteHistoryEntry[];
    } catch {
      return [];
    }
  });

  // Track if this event version has been seen (for immediate UI update)
  const [hasBeenSeen, setHasBeenSeen] = useState(() =>
    isEventSeen(event.uuid, event.version),
  );

  // Reload mute history when the event changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(
        getMuteHistoryStorageKey(event.uuid),
      );
      if (!raw) {
        setMuteHistory([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setMuteHistory([]);
        return;
      }
      setMuteHistory(parsed as MuteHistoryEntry[]);
    } catch {
      setMuteHistory([]);
    }
  }, [event.uuid]);

  // Update hasBeenSeen when event version changes
  useEffect(() => {
    setHasBeenSeen(isEventSeen(event.uuid, event.version));
  }, [event.uuid, event.version]);

  // Determine if event was recently changed based on updatedAt timestamp and seen status.
  // We only consider it "changed" if there is a previous version of the event.
  const changed = useMemo(() => {
    if (hasBeenSeen) return false;
    if (!previousEvent) return false;
    return isRecentlyChanged(event);
  }, [hasBeenSeen, event, previousEvent]);

  const addMuteHistoryEntry = useCallback(
    (action: MuteHistoryEntry["action"], reason?: string) => {
      const key = getMuteHistoryStorageKey(event.uuid);
      setMuteHistory((prev) => {
        const next: MuteHistoryEntry[] = [
          ...prev,
          { timestamp: new Date().toISOString(), action, reason },
        ];

        if (typeof window !== "undefined") {
          try {
            window.localStorage.setItem(key, JSON.stringify(next));
          } catch {
            // Ignore write errors (e.g. storage full)
          }
        }

        notifyMuteChanged();

        return next;
      });
    },
    [event.uuid, notifyMuteChanged],
  );

  const handleDialogConfirmed = useCallback(() => {
    setHasBeenSeen(true);
    setChangeDialogOpen(false);
  }, []);

  const handleMuted = useCallback(
    (reason: string) => {
      setNotifying(false);
      addMuteHistoryEntry("muted", reason);
    },
    [addMuteHistoryEntry],
  );

  const handleUnmuted = useCallback(
    (reason?: string) => {
      setNotifying(columnEventState === "noCoverage");
      addMuteHistoryEntry("unmuted", reason);
    },
    [addMuteHistoryEntry, columnEventState],
  );

  // Set notifying based on mute history length
  useEffect(() => {
    if (muteHistory.length % 2 === 0 && columnEventState === "noCoverage") {
      setNotifying(true);
    } else {
      setNotifying(false);
    }
  }, [muteHistory, columnEventState]);

  const muted =
    muteHistory.length > 0 &&
    muteHistory[muteHistory.length - 1].action === "muted";

  return {
    changed,
    notifying,
    setNotifying,
    muteDialogOpen,
    setMuteDialogOpen,
    muted,
    changeDialogOpen,
    setChangeDialogOpen,
    muteHistory,
    addMuteHistoryEntry,
    handleDialogConfirmed,
    handleMuted,
    handleUnmuted,
  };
};
